import {
  type ActivityLogEntry,
  type Archetype,
  type Deck,
  type DeckAnalysis,
  type DeckCreate,
  type DeckUpdate,
  type Format,
  type MatchupCell,
  type NearestMetaArchetype,
} from '@ugin/shared';

import { viteEnv } from './env';
import {
  type ArchetypeView,
  type HealthView,
  type MatchDetail,
  type MatchSummary,
  type StatsResponse,
} from './types';

export interface ApiClientOptions {
  baseUrl?: string;
  fetch?: typeof fetch;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * A 401 from any call, typed so the app can route to sign-in on this and only
 * this. Every other failure is an ApiError the calling screen handles itself.
 */
export class SessionExpired extends ApiError {
  constructor(message = 'Your session has expired.', details?: unknown) {
    super(401, 'unauthorized', message, details);
    this.name = 'SessionExpired';
  }
}

interface ApiErrorBody {
  error?: { code?: string; message?: string; details?: unknown };
}

export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  pictureUrl: string | null;
  role: string;
}

/**
 * Any call can be the one that discovers the session is gone — a cookie
 * expires on its own schedule, not on the app's. Subscribers are notified so
 * the shell can return to sign-in from wherever the user happened to be.
 */
const sessionExpiredListeners = new Set<() => void>();

export function onSessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly injectedFetch: typeof fetch | undefined;

  constructor(opts: ApiClientOptions = {}) {
    const env = viteEnv();
    this.baseUrl = (opts.baseUrl ?? env.VITE_DATA_API_URL ?? '/').replace(/\/$/, '');
    this.injectedFetch = opts.fetch;
  }

  /**
   * Resolved per call, not bound in the constructor: the exported singleton is
   * built when this module loads, and binding then would freeze whatever
   * `fetch` existed at import — unreachable by any later instrumentation.
   */
  private get fetchImpl(): typeof fetch {
    return this.injectedFetch ?? globalThis.fetch.bind(globalThis);
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    init?: RequestInit,
  ): Promise<T> {
    const url = path.startsWith('http') ? path : `${this.baseUrl}${path}`;
    const headers = new Headers(init?.headers ?? {});
    headers.set('Accept', 'application/json');
    if (body !== undefined) headers.set('Content-Type', 'application/json');

    const resp = await this.fetchImpl(url, {
      ...init,
      method,
      headers,
      body: body === undefined ? null : JSON.stringify(body),
      // The session cookie is HttpOnly: the app cannot read it and cannot
      // attach it by hand, so omitting credentials logs every request out.
      credentials: 'include',
    });

    if (resp.status === 204) return undefined as T;

    const text = await resp.text();
    const parsed: unknown = text.length > 0 ? JSON.parse(text) : null;

    if (!resp.ok) {
      const errorBody = parsed as ApiErrorBody | null;
      const message = errorBody?.error?.message ?? (resp.statusText || 'Request failed');
      if (resp.status === 401) {
        for (const listener of sessionExpiredListeners) listener();
        throw new SessionExpired(message, errorBody?.error?.details);
      }
      throw new ApiError(
        resp.status,
        errorBody?.error?.code ?? `http_${resp.status}`,
        message,
        errorBody?.error?.details,
      );
    }
    return parsed as T;
  }

  health = (): Promise<HealthView> => this.request('GET', '/api/health');

  auth = {
    me: (): Promise<{ user: SessionUser }> => this.request('GET', '/api/auth/me'),
    google: (idToken: string): Promise<{ user: SessionUser }> =>
      this.request('POST', '/api/auth/google', { idToken }),
    logout: (): Promise<void> => this.request('POST', '/api/auth/logout'),
  };

  decks = {
    list: (params?: { format?: Format; limit?: number }): Promise<{ decks: Deck[] }> => {
      const search = new URLSearchParams();
      if (params?.format) search.set('format', params.format);
      if (params?.limit) search.set('limit', String(params.limit));
      const qs = search.toString();
      return this.request('GET', `/api/decks${qs ? `?${qs}` : ''}`);
    },
    get: (id: string): Promise<{ deck: Deck }> => this.request('GET', `/api/decks/${id}`),
    create: (payload: DeckCreate): Promise<{ deck: Deck }> =>
      this.request('POST', '/api/decks', payload),
    update: (id: string, payload: DeckUpdate): Promise<{ deck: Deck }> =>
      this.request('PATCH', `/api/decks/${id}`, payload),
    delete: (id: string): Promise<void> => this.request('DELETE', `/api/decks/${id}`),
    analysis: (id: string): Promise<{ analysis: DeckAnalysis; cached: boolean }> =>
      this.request('GET', `/api/decks/${id}/analysis`),
    similar: (
      id: string,
    ): Promise<{
      format: Format;
      /** `characterized` compares against real decklists; `snapshot` cannot. */
      source: 'characterized' | 'snapshot' | 'none';
      results: {
        archetype: string;
        overlapPercent: number;
        missingCards: { cardName: string; quantity: number }[];
        extraCards: { cardName: string; quantity: number }[];
      }[];
      nearest: NearestMetaArchetype | null;
      message?: string;
      computedAt?: string | null;
      snapshotAt?: string;
      snapshotSource?: string;
    }> => this.request('GET', `/api/decks/${id}/similar`),
  };

  meta = {
    forFormat: (format: Format): Promise<{
      format: Format;
      archetypes: Archetype[];
      sources: { source: string; scrapedAt: string }[];
      empty: boolean;
    }> => this.request('GET', `/api/meta/${format}`),
  };

  archetypes = {
    forFormat: (
      format: Format,
    ): Promise<{ format: Format; computedAt: string | null; archetypes: ArchetypeView[] }> =>
      this.request('GET', `/api/meta/${format}/archetypes`),
  };

  matches = {
    list: (params?: {
      format?: Format;
      deckId?: string;
      limit?: number;
      offset?: number;
    }): Promise<{ total: number; matches: MatchSummary[] }> => {
      const search = new URLSearchParams();
      if (params?.format) search.set('format', params.format);
      if (params?.deckId) search.set('deckId', params.deckId);
      if (params?.limit) search.set('limit', String(params.limit));
      if (params?.offset) search.set('offset', String(params.offset));
      const qs = search.toString();
      return this.request('GET', `/api/matches${qs ? `?${qs}` : ''}`);
    },
    get: (id: string): Promise<{ match: MatchDetail }> => this.request('GET', `/api/matches/${id}`),
  };

  stats = {
    overview: (format?: Format): Promise<StatsResponse> =>
      this.request('GET', `/api/stats${format ? `?format=${format}` : ''}`),
  };

  matchups = {
    list: (params?: { format?: Format; deckId?: string }): Promise<{
      format?: Format;
      cells: MatchupCell[];
      generatedAt: string;
      sampleSize: number;
    }> => {
      const search = new URLSearchParams();
      if (params?.format) search.set('format', params.format);
      if (params?.deckId) search.set('deckId', params.deckId);
      const qs = search.toString();
      return this.request('GET', `/api/matchups${qs ? `?${qs}` : ''}`);
    },
  };

  logs = {
    recent: (limit = 50): Promise<{ entries: ActivityLogEntry[] }> =>
      this.request('GET', `/api/logs/recent?limit=${limit}`),
  };
}

export const api = new ApiClient();
